import type { Estimation } from '../api/generated/model'

/** The estimation kinds, with the wording a person would use to choose between them. */
export const ESTIMATION_CHOICES = [
  { value: 'FIXED', label: 'Always the same amount' },
  { value: 'TRAILING_MEAN', label: 'Average of recent ones' },
  { value: 'TRAILING_MEDIAN', label: 'Typical recent one — ignores a one-off spike' },
  { value: 'TRAILING_MAX', label: 'Worst recent one — plans for the bad month' },
  { value: 'PERCENTILE', label: 'Worse than most recent ones' },
  { value: 'SEASONAL', label: 'Same time of year, previous years' },
  { value: 'TRAILING_MEAN_WITH_GROWTH', label: 'Average, adjusted upward each year' },
] as const

export type EstimationKind = (typeof ESTIMATION_CHOICES)[number]['value']

export type EstimationDraft = {
  kind: EstimationKind
  sample: string
  percentile: string
  years: string
  growthPercent: string
}

export function blankEstimation(): EstimationDraft {
  return { kind: 'FIXED', sample: '6', percentile: '80', years: '2', growthPercent: '3' }
}

export function estimationDraftFrom(estimation: Estimation | null | undefined): EstimationDraft {
  const blank = blankEstimation()
  if (!estimation) return blank
  return {
    kind: estimation.kind,
    sample: String(estimation.sample ?? blank.sample),
    percentile: String(estimation.percentile ?? blank.percentile),
    years: String(estimation.years ?? blank.years),
    growthPercent: String(Number(estimation.growthRate ?? 0.03) * 100),
  }
}

/** Undefined means a stated amount, which the API expresses by omitting the field. */
export function toEstimation(draft: EstimationDraft): Estimation | undefined {
  if (draft.kind === 'FIXED') return undefined
  const sample = Number(draft.sample) || 6
  switch (draft.kind) {
    case 'PERCENTILE':
      return { kind: 'PERCENTILE', sample, percentile: Number(draft.percentile) || 80 }
    case 'SEASONAL':
      return { kind: 'SEASONAL', years: Number(draft.years) || 2 }
    case 'TRAILING_MEAN_WITH_GROWTH':
      return {
        kind: 'TRAILING_MEAN_WITH_GROWTH',
        sample,
        growthRate: (Number(draft.growthPercent) || 0) / 100,
      }
    default:
      return { kind: draft.kind, sample }
  }
}
